import Email, { MailsListId } from "@/domain/entities/emails/Email";
import { calculateMasonryLayout } from "@/lib/masonery";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { FocusTarget } from "./MailAction";
import MailTile from "./MailTile";

type EmailMosaicProps = {
    selectedMails: Email[];
    handleCheckboxSelectChange: (checked: boolean, mail: Email) => void;
    columns?: number;
    mailsListFlattened: Email[];
    listId: MailsListId;
    updateVersion?: number;
    setMailInputExpanded: (
        mailInputExpanded: boolean,
        focusTarget?: FocusTarget,
    ) => void;
    setSelectedMails: Dispatch<SetStateAction<Email[]>>;
};

const EmailMosaic = ({
    selectedMails,
    handleCheckboxSelectChange,
    columns,
    mailsListFlattened,
    listId,
    updateVersion,
    setMailInputExpanded,
    setSelectedMails,
}: EmailMosaicProps) => {
    const [screenWidth, setScreenWidth] = useState<number>(
        typeof window !== "undefined" ? window.innerWidth : 0,
    );

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const gridColumns = useMemo(() => {
        if (columns) {
            return columns;
        }

        if (screenWidth < 768) {
            return 2;
        }

        if (screenWidth < 1280) {
            return 3;
        }

        return 4;
    }, [screenWidth]);

    const tileLayouts = useMemo(
        () => calculateMasonryLayout(mailsListFlattened, gridColumns),
        [mailsListFlattened, gridColumns, updateVersion],
    );

    const gridHeight = useMemo(() => {
        let maxRow = 0;
        tileLayouts.forEach((layout) => {
            maxRow = Math.max(maxRow, layout.rowStart + layout.rowSpan - 1);
        });

        return maxRow;
    }, [tileLayouts]);

    return (
        <div
            className="grid w-full gap-3"
            style={{
                gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${gridHeight}, auto)`,
            }}
        >
            {mailsListFlattened.map((mail) => {
                const layout = tileLayouts.get(mail.id);

                if (!layout) {
                    console.warn(`Layout not found for mail ${mail.id}`);

                    return null;
                }

                return (
                    <div
                        key={mail.uniqueMailvistaId}
                        className="m-auto w-full"
                        style={{
                            gridColumnStart: layout.colStart,
                            gridColumnEnd: layout.colStart + layout.colSpan,
                            gridRowStart: layout.rowStart,
                            gridRowEnd: layout.rowStart + layout.rowSpan,
                        }}
                    >
                        <MailTile
                            listId={listId}
                            mail={mail}
                            onSelectChange={handleCheckboxSelectChange}
                            selectedMails={selectedMails}
                            setMailInputExpanded={setMailInputExpanded}
                            setSelectedMails={setSelectedMails}
                            size={mail.componentSize}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default EmailMosaic;
